/**
 * <PageLoader /> — full-page Suspense fallback.
 *
 * Replaces the bare "Loading…" text that App.tsx was using. Shows a skeleton
 * roughly mimicking MCT's typical page layout (top stat row + a couple of
 * panels + a chart-sized block) plus a centered branded spinner so the user
 * sees it's actively loading rather than stalled.
 *
 * Used as the fallback for the route-level <Suspense> in App.tsx — fires
 * during the lazy() chunk download for the destination page.
 */
import Skeleton from "./Skeleton";

interface Props {
  /** Optional label shown next to the spinner (e.g. "Loading P&L…"). */
  label?: string;
  /** When true, hides the skeleton scaffolding and just shows the spinner.
      Useful for short waits where the skeleton would flash and look worse. */
  minimal?: boolean;
}

export default function PageLoader({ label = "Loading", minimal = false }: Props) {
  if (minimal) {
    return (
      <div className="flex-1 flex items-center justify-center text-zinc-500 text-sm gap-2">
        <Spinner />
        <span>{label}…</span>
      </div>
    );
  }

  return (
    <div className="flex-1 flex flex-col gap-4">
      {/* Top stat row */}
      <Skeleton variant="stat-tile" count={5} />

      {/* Two columns of panels */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Skeleton variant="panel" h="180px" className="lg:col-span-2" />
        <Skeleton variant="panel" h="180px" />
      </div>

      {/* Chart-sized block */}
      <Skeleton variant="chart" h="240px" />

      {/* Centered spinner overlay */}
      <div className="fixed inset-0 pointer-events-none flex items-center justify-center">
        <div className="flex items-center gap-3 px-4 py-2.5 rounded-full bg-zinc-900/85 border border-zinc-800/70 shadow-xl backdrop-blur-sm">
          <Spinner />
          <span className="text-zinc-300 text-xs font-semibold tracking-wide uppercase">{label}…</span>
        </div>
      </div>
    </div>
  );
}

/** Small branded spinner — concentric ring with brand-colour accent. */
function Spinner() {
  return (
    <span
      className="inline-block w-4 h-4 rounded-full border-2 border-zinc-700 border-t-brand animate-spin"
      aria-label="loading"
    />
  );
}
